import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PriceCard from '@/components/feature/PriceCard';
import { priceList } from '@/mocks/priceData';
import { newsList } from '@/mocks/newsData';
import type { PriceItem } from '@/types';

type TypeFilter = 'all' | 'spot' | 'futures';
type RangeFilter = 'today' | 'week' | 'month';
type SortKey = 'default' | 'up' | 'down';

const typeTabs: { key: TypeFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'spot', label: '现货' },
  { key: 'futures', label: '期货' },
];

const rangeTabs: { key: RangeFilter; label: string }[] = [
  { key: 'today', label: '今日' },
  { key: 'week', label: '近周' },
  { key: 'month', label: '近月' },
];

const sortTabs: { key: SortKey; label: string }[] = [
  { key: 'default', label: '默认排序' },
  { key: 'up', label: '涨幅优先' },
  { key: 'down', label: '跌幅优先' },
];

const rangePoints: Record<RangeFilter, number> = { today: 6, week: 9, month: 12 };

export default function GoldPriceCategory() {
  const [type, setType] = useState<TypeFilter>('all');
  const [range, setRange] = useState<RangeFilter>('today');
  const [sort, setSort] = useState<SortKey>('default');
  const [visible, setVisible] = useState(6);

  const filtered = useMemo(() => {
    let list = (priceList as PriceItem[]).filter((item) => type === 'all' || item.type === type);
    if (sort === 'up') list = [...list].sort((a, b) => b.changePercent - a.changePercent);
    if (sort === 'down') list = [...list].sort((a, b) => a.changePercent - b.changePercent);
    return list.map((item) => ({ ...item, trend: item.trend.slice(-rangePoints[range]) }));
  }, [type, range, sort]);

  const shown = filtered.slice(0, visible);
  const hasMore = filtered.length > visible;
  const relatedNews = (newsList as typeof newsList).slice(0, 5);

  const reset = () => {
    setType('all');
    setRange('today');
    setSort('default');
    setVisible(6);
  };

  return (
    <Layout>
      <title>黄金行情 - 全部分类 - 黄金</title>
      <meta
        name="description"
        content="黄金行情分类页汇总黄金现货、黄金期货等主流品种最新价格、涨跌幅与走势，数据每日多时段更新，助你快速把握黄金市场动向。"
      />
      <meta name="keywords" content="黄金行情,黄金价格,现货黄金,黄金期货,金价走势" />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-portal px-4 py-6 md:px-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">黄金行情</h1>
          <div className="mt-3">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '黄金行情' }]} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-portal px-4 py-8 md:px-6 md:py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_300px]">
          <div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-foreground-700">行情类型</span>
                  <div className="flex rounded-full border border-background-300 p-1">
                    {typeTabs.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => { setType(item.key); setVisible(6); }}
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          type === item.key ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-primary-700'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-foreground-700">时间区间</span>
                  <div className="flex rounded-full border border-background-300 p-1">
                    {rangeTabs.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setRange(item.key)}
                        className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          range === item.key ? 'bg-secondary-500 text-background-50' : 'text-foreground-600 hover:text-primary-700'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-medium text-foreground-700">涨跌排序</span>
                  <div className="flex flex-wrap gap-2">
                    {sortTabs.map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setSort(item.key)}
                        className={`rounded-md border px-3 py-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                          sort === item.key
                            ? 'border-primary-400 bg-primary-50 text-primary-800'
                            : 'border-background-300 text-foreground-600 hover:border-primary-300'
                        }`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between text-sm text-foreground-500">
              <span>共 {filtered.length} 个品种 · 含演示数据</span>
              <span>数据更新至 2026-10-12 15:30</span>
            </div>

            {shown.length > 0 ? (
              <>
                <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {shown.map((item) => (
                    <PriceCard key={item.id} item={item} />
                  ))}
                </div>
                {hasMore && (
                  <div className="mt-6 text-center">
                    <button
                      type="button"
                      onClick={() => setVisible((prev) => prev + 4)}
                      className="inline-flex items-center gap-1.5 rounded-md border border-primary-300 bg-primary-50 px-6 py-2.5 text-sm font-semibold text-primary-800 transition-colors hover:bg-primary-100 whitespace-nowrap cursor-pointer"
                    >
                      加载更多
                      <i className="ri-arrow-down-line" aria-hidden="true"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                  <i className="ri-inbox-line" aria-hidden="true"></i>
                </span>
                <p className="mt-4 text-base font-medium text-foreground-800">暂无匹配的行情品种</p>
                <p className="mt-1 text-sm text-foreground-500">试试调整筛选条件，或重置查看全部行情。</p>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-5 rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400 whitespace-nowrap cursor-pointer"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
                <i className="ri-newspaper-line text-primary-600" aria-hidden="true"></i>
                相关资讯
              </h2>
              <ul className="mt-3 divide-y divide-background-200">
                {relatedNews.map((news) => (
                  <li key={news.id} className="py-3 first:pt-0 last:pb-0">
                    <Link to={`/detail/news/${news.id}`} className="group flex gap-2">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"></span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                          {news.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-400">{news.date}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}